import React, { Component } from 'react';
import { func, number, object, bool, string } from 'prop-types';
import classNames from 'classnames';
import I18n from '@fiverr-private/fiverr_context/src/I18n';
import { gofor } from '@fiverr-private/futile/lib/gofor';
import { localStorage } from '@fiverr-private/futile/lib/storage';
import SmallGigCardCarousel from '@fiverr-private/gig_listings/src/entries/SmallGigCardCarousel';
import { GIG_GRID_LRG, GIG_GRID_MED, GIG_GRID_SML, GIG_GRID_XLRG } from '../shared/Grid';
import SafePureComponent from '../shared/SafePureComponent';
import contextEnrichment from '../../utils/contextEnrichment';
import getGigs from '../../utils/gigCardCarouselService';
import { RECENTLY_VIEWED } from '../../utils/carousels';

import './style.scss';

const RECENTLY_VIEWED_PAGE_PATH = '/pages/recently_viewed';

const RECENTLY_VIEWED_SOURCE = 'your_recently_viewed_gigs';

const sliderBaseNum = 8;

const sliderBreakpoints = [
    {
        breakpoint: GIG_GRID_XLRG,
        slides: 6
    },
    {
        breakpoint: GIG_GRID_LRG,
        slides: 5
    },
    {
        breakpoint: GIG_GRID_MED,
        slides: 4
    },
    {
        breakpoint: GIG_GRID_SML,
        slides: 2
    }
];

export const LOCAL_STORAGE_KEY = 'browse-history';
const GIGS_REGEX = /gigs\|\|\|show\|\|\|([\d]+)/;

const getGigIds = (context) => {
    // this method returns ls gig ids excluding current gig id
    const { general } = context;
    const { gigId } = general;
    const gigIdStr = gigId.toString();
    const userBrowseHistory = localStorage.get(LOCAL_STORAGE_KEY);

    if (userBrowseHistory) {
        return userBrowseHistory.reduce((accumulator, data) => {
            const match = data.match(GIGS_REGEX);

            if (match && match.length === 2 && match[1] !== gigIdStr) {
                accumulator.push(match[1]);
            }

            return accumulator;
        }, []);
    } else {
        return [];
    }
};

class RecentlyViewedGigs extends Component {

    constructor(props, context) {
        super(props);

        let isLoading = true;

        if (!props.isLoggedIn && !getGigIds(context).length) {
            isLoading = false;
        }

        this.state = {
            isDeleted: false,
            isTotallyDeleted: false,
            gigs: [],
            isLoading
        };

        this.onClearAllClick = this.onClearAllClick.bind(this);
        this.getRecentlyUrl = this.getRecentlyUrl.bind(this);
        this.clearRecentlyViewedGig = this.clearRecentlyViewedGig.bind(this);
    }

    componentDidMount() {
        const { isLoading } = this.state;
        if (!isLoading) {
            return;
        }

        const url = this.getRecentlyUrl();

        getGigs({ url, type: RECENTLY_VIEWED })
            .then((gigs) => {
                this.setState({
                    gigs,
                    isLoading: false
                });
            })
            .catch(() => {
                this.setState({
                    isLoading: false
                });
            });
    }

    getRecentlyUrl() {
        const { isLoggedIn } = this.props;
        const { pathfinderWrapper, general } = this.context;
        const query = { exclude: general.gigId };

        if (!isLoggedIn) {
            query.gig_ids = getGigIds(this.context);
        }

        return pathfinderWrapper.pathfinder(
            'gig_page_fetch_recommendations_by_type',
            { type: 'recently_viewed' },
            { query }
        );
    }

    onClearAllClick(e) {
        e && e.preventDefault();
        const { pathfinderWrapper, logger } = this.context;
        const { isLoggedIn } = this.props;

        if (isLoggedIn) {
            const url = pathfinderWrapper.pathfinder('clear_recently_viewed_gigs');

            gofor(url, {
                method: 'DELETE'
            }).catch((error) => {
                logger.error(error);
            });
        } else {
            const updatedHistory = localStorage.get(LOCAL_STORAGE_KEY)
                .filter((entry) => !entry.match(GIGS_REGEX));

            localStorage.set(LOCAL_STORAGE_KEY, updatedHistory);
        }

        this.hideComponent();
    }

    hideComponent() {
        /*
            we set isDeleted first which triggers a 2 second fade out
            then we set isTotallyDeleted to remove the entire element
        */

        this.setState({
            isDeleted: true
        }, () => {
            setTimeout(() => {
                this.setState({
                    isTotallyDeleted: true
                });
            }, 2000);
        });
    }

    clearRecentlyViewedGig(gigId) {
        const gigIdString = gigId.toString();
        const updatedHistory = localStorage.get(LOCAL_STORAGE_KEY)
            .filter((entry) => {
                const match = entry.match(GIGS_REGEX);
                return !(match && match[1] === gigIdString);
            });

        localStorage.set(LOCAL_STORAGE_KEY, updatedHistory);

        if (!getGigIds(this.context).length) {
            this.hideComponent();
        }
    }

    render() {
        const {
            collections,
            isLoggedIn,
            isTouch,
            updateCollections
        } = this.props;

        const {
            isDeleted, // this only applies if the user clears all the gigs
            isTotallyDeleted, // this only applies if the user clears all the gigs
            gigs,
            isLoading
        } = this.state;

        if (isTotallyDeleted || (!gigs.length && !isLoading)) {
            return null;
        }

        const boxClasses = classNames('gigs-recently-viewed', { 'deleted': isDeleted });

        const contextEnrichmentProps = {
            carouselPosition: 4,
            collections,
            isLoggedIn,
            isTouch,
            updateCollections,
            source: RECENTLY_VIEWED_SOURCE
        };

        const carouselContext = contextEnrichment({ ...contextEnrichmentProps, ...this.context });

        // Currently logged in Api is implemented as default in gig cards package
        const onCarouselItemClear = isLoggedIn ? undefined : this.clearRecentlyViewedGig;

        return (
            <div className={boxClasses}>

                <h2 className="section-title">
                    {!isDeleted &&
                    <small>
                        <a href="#!" onClick={this.onClearAllClick}>
                            <I18n k={'gig_page_perseus.general.clear_all'}/>
                        </a>
                        {isLoggedIn &&
                        <a className="see-all" href={RECENTLY_VIEWED_PAGE_PATH}>
                            <I18n k={'gig_page_perseus.general.see_all'}/>
                        </a>
                        }
                    </small>
                    }
                    <I18n k={'gig_page_perseus.recommended_gigs.your_browsing_history.title'}/>
                </h2>
                <SmallGigCardCarousel gigs={gigs}
                    sliderBaseNum={sliderBaseNum}
                    isLoading={isLoading}
                    sliderBreakpoints={sliderBreakpoints}
                    onClearGig={onCarouselItemClear}
                    lazyLoad={true}
                    {...carouselContext}/>
            </div>
        );
    }
}

RecentlyViewedGigs.propTypes = {
    collections: object,
    isLoggedIn: bool,
    isTouch: bool,
    updateCollections: func,
    position: number,
    source: string
};

RecentlyViewedGigs.defaultProps = {
    isTouch: false,
    collections: {}
};

RecentlyViewedGigs.contextTypes = {
    logger: object,
    requestContext: object,
    general: object,
    currencyConverter: object,
    pathfinderWrapper: object
};

export { RecentlyViewedGigs };
export default SafePureComponent(RecentlyViewedGigs);
